import { Button, message } from "antd";
import React, { useState } from "react";
import styled from "styled-components";

type NotificationType = "consent" | "invoice";

interface NotificationItem {
  id: number;
  type: NotificationType;
}

export const DefaultNotifications: React.FC = () => {
  const [popup, setPopup] = useState<NotificationType | null>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([
    { id: 1, type: "consent" },
    { id: 2, type: "invoice" }
  ]);

  const removeNotification = (type: NotificationType) => {
    setNotifications(prev => prev.filter(n => n.type !== type));
    setPopup(null);
  };

  const approveConsent = () => {
    message.success("Consent approved successfully.");
    removeNotification("consent");
  };

  const declineConsent = () => {
    message.info("Consent declined.");
    removeNotification("consent");
  };

  const approvePayment = () => {
    message.success("Payment link has been sent to your email.");
    removeNotification("invoice");
  };

  const denyPayment = () => {
    message.error("Payment request denied.");
    removeNotification("invoice");
  };

  return (
    <>
      <Wrapper>
        {notifications.some(n => n.type === "consent") && (
          <Card>
            <Title>NG Tube Procedure Consent Required</Title>

            <Description>
              The care team has requested your approval for the Nasogastric Tube
              (NGT) procedure. Please review and sign the consent document
              before the procedure can be performed.
            </Description>

            <Info>
              <Row>
                <Label>Order ID:</Label>
                <Value>NGT-2026-001</Value>
              </Row>

              {/* <Row>
                <Label>Requested by:</Label>
                <Value>Nurse Supervisor</Value>
              </Row>

              <Row>
                <Label>Patient:</Label>
                <Value>Mr Lim Chee Seng</Value>
              </Row> */}

              <Row>
                <Label>Procedure:</Label>
                <Value>Nasogastric Tube Insertion</Value>
              </Row>
            </Info>

            <ButtonRow>
              <PrimaryButton onClick={() => setPopup("consent")}>
                Review Consent
              </PrimaryButton>
            </ButtonRow>
          </Card>
        )}

        {notifications.some(n => n.type === "invoice") && (
          <Card>
            <Title>Invoice Payment Required</Title>

            <Description>
              An invoice has been generated for the Nasogastric Tube procedure.
              Please review and complete the payment.
            </Description>

            <Info>
              <Row>
                <Label>Invoice Number:</Label>
                <Value>INV-2026-0451</Value>
              </Row>

              <Row>
                <Label>Amount Due:</Label>
                <Value>SGD 241.15</Value>
              </Row>

              <Row>
                <Label>Due Date:</Label>
                <Value>30 March 2026</Value>
              </Row>
            </Info>

            <ButtonRow>
              <PrimaryButton onClick={() => setPopup("invoice")}>
                Pay Invoice
              </PrimaryButton>
            </ButtonRow>
          </Card>
        )}
      </Wrapper>

      {/* CONSENT POPUP */}
      {popup === "consent" && (
        <Overlay>
          <Popup>
            <Header>
              Nasogastric Tube Insertion Consent Form
              <Close onClick={() => setPopup(null)}>×</Close>
            </Header>

            <InfoBox>
              This procedure involves inserting a feeding tube through the nose
              into the stomach to provide nutritional support.
            </InfoBox>

            <Text>By approving this consent, you acknowledge that:</Text>

            <ul>
              <li>The risks and benefits have been explained to you.</li>
              <li>You have had the opportunity to ask questions.</li>
              <li>You authorize the medical team to perform the procedure.</li>
            </ul>

            <ActionRow>
              <ApproveButton onClick={approveConsent}>
                Approve Consent
              </ApproveButton>

              <DeclineButton onClick={declineConsent}>Decline</DeclineButton>
            </ActionRow>
          </Popup>
        </Overlay>
      )}

      {/* INVOICE POPUP */}
      {popup === "invoice" && (
        <Overlay>
          <Popup>
            <Header>
              Invoice Payment
              <Close onClick={() => setPopup(null)}>×</Close>
            </Header>

            <Tabs>
              <Tab active>PayNow</Tab>
              <Tab>Credit Card</Tab>
              <Tab>Bank Transfer</Tab>
            </Tabs>

            <AmountBox>
              <RowBetween>
                <span>Services after subsidy</span>
                <span>SGD 203.00</span>
              </RowBetween>

              <RowBetween>
                <span>Medical Consumables</span>
                <span>SGD 35.00</span>
              </RowBetween>

              <RowBetween>
                <span>GST (9%)</span>
                <span>SGD 3.15</span>
              </RowBetween>

              <Divider />

              <RowBetween>
                <Total>Total Amount</Total>
                <Total>SGD 241.15</Total>
              </RowBetween>
            </AmountBox>

            <ActionRow>
              <ApproveButton onClick={approvePayment}>
                Approve Payment
              </ApproveButton>

              <DeclineButton onClick={denyPayment}>Deny Payment</DeclineButton>
            </ActionRow>
          </Popup>
        </Overlay>
      )}
    </>
  );
};

export default DefaultNotifications;

/* STYLES */

const Wrapper = styled.div`
  padding: 12px;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 16px;
`;

const Description = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: #6b7280;
`;

const Info = styled.div`
  margin-top: 10px;
`;

const Row = styled.div`
  font-size: 13px;
  margin-bottom: 3px;
`;

const Label = styled.span`
  color: #9ca3af;
  margin-right: 6px;
`;

const Value = styled.span`
  font-weight: 500;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const PrimaryButton = styled(Button)`
  background: #1976d2 !important;
  color: white !important;
`;

const SecondaryButton = styled(Button)`
  background: #f3f4f6 !important;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: grid;
  place-items: center;
  z-index: 1000;
`;

const Popup = styled.div`
  width: 420px;
  background: white;
  border-radius: 16px;
  padding: 20px;
`;

const Header = styled.div`
  font-weight: 600;
  font-size: 18px;
  display: flex;
  justify-content: space-between;
`;

const Close = styled.span`
  cursor: pointer;
`;

const InfoBox = styled.div`
  background: #eef4ff;
  padding: 12px;
  border-radius: 8px;
  margin-top: 14px;
`;

const Text = styled.div`
  margin-top: 12px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 16px;
`;

const ApproveButton = styled(Button)`
  background: #1976d2 !important;
  color: white !important;
`;

const DeclineButton = styled(Button)`
  background: #f3f4f6 !important;
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin: 14px 0;
`;

const Tab = styled.div<{ active?: boolean }>`
  padding: 8px 14px;
  border-radius: 8px;
  background: ${({ active }) => (active ? "#e6f0ff" : "#f3f4f6")};
  cursor: pointer;
`;

const AmountBox = styled.div`
  background: #f8fafc;
  padding: 12px;
  border-radius: 10px;
`;

const RowBetween = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const Total = styled.span`
  font-weight: 600;
  font-size: 16px;
`;

const Divider = styled.hr`
  margin: 10px 0;
`;

const ConfirmButton = styled(Button)`
  width: 100%;
  margin-top: 14px !important;
  background: #1976d2 !important;
  color: white !important;
`;
